import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { DateTimeService } from 'i2g-ng-utils';
import { fromJS, List, Map } from 'immutable';
import { forEach, get } from 'lodash';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { delay, filter, map, takeUntil, tap } from 'rxjs/operators';
import { Account } from '../../../account-data/account.model';
import {
  DialogActionsEnum,
  DIALOG_ANIMATE_TIME,
  IDialogData,
  IDialogTrigger,
} from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import {
  DepositTypesEnum,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DepositPayment } from '../../../document-data/sub-models/deposit-payment.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { IDatePickerFieldOptions } from '../../../form-master/components/date-picker-field/date-picker-field.component';
import {
  IFormFieldErrorMessages,
  IFormValidators,
} from '../../../form-master/form-master.interface';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { CalculatorService } from '../../../shared/services/calculator/calculator.service';
import { MoneyService } from '../../../shared/services/money/money.service';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DepositRequestDialogBuilder } from './deposit-request-dialog.builder';
import {
  DepositRequestTabsEnum,
  IDepositRequestTab,
} from './deposit-request-dialog.data';

export const DIALOG_TYPE = 'deposit-request';
export const DEFAULT_PERCENTAGE = 50;

@Component({
  selector: 'app-deposit-request-dialog',
  templateUrl: './deposit-request-dialog.component.html',
  styleUrls: ['./deposit-request-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DepositRequestDialogBuilder],
})
export class DepositRequestDialogComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() depositRequestFeatureName: string;
  @Input() documentCurrencyCode: string;
  @Input() documentLocale: string;
  @Input() documentDueDate: string;
  @Input() documentAllAccess: boolean;
  @Input() addDepositForFuture: DepositToggleValue;
  @Input() deposit: Deposit;
  @Input() featureToggles: Toggles;
  @Input() documentType: DocumentTypesEnum;
  @Input() documentCalculationResult: CalculationResult;
  @Input() account: Account;
  @Input() trackingDialogName: TrackingDialogNamesEnum;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() documentTrackingScreenName: TrackingScreenNamesEnum;

  @Output() onSaveRequest: EventEmitter<Deposit> = new EventEmitter<Deposit>();
  @Output() onUpdateDepositToggleSettings: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() onCancelRequest: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _selectTab: BehaviorSubject<DepositRequestTabsEnum> = new BehaviorSubject<DepositRequestTabsEnum>(
    DepositRequestTabsEnum.percent
  );
  private _lastModifiedDepositType: DepositTypesEnum =
    DepositTypesEnum.percentage;
  private _startingFormValue: any;
  private _percentagePercentControl: AbstractControl;
  private _percentageFixedAmountControl: AbstractControl;
  private _fixedAmountControl: AbstractControl;
  private _dueDateControl: AbstractControl;
  private _addDepositForFutureControl: AbstractControl;

  dialogType: string;
  documentTotalLabel: string;
  addDepositForFutureLabel: string;
  cancellable: boolean;
  depositSavable: boolean;
  depositToggleAccess: boolean;
  formKeys: any;
  form: FormGroup;
  tabs: List<IDepositRequestTab>;
  depositDateDatePickerOptions: IDatePickerFieldOptions;
  DepositRequestTabsEnum: typeof DepositRequestTabsEnum;
  selectedTab: DepositRequestTabsEnum;
  trackingScreenName: TrackingScreenNamesEnum;
  primaryActionTapped: ITrackingAction;
  secondaryActionTapped: ITrackingAction;
  amountErrorMessages: IFormFieldErrorMessages;
  percentageErrorMessages: IFormFieldErrorMessages;

  constructor(
    private _depositRequestDialogBuilder: DepositRequestDialogBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _calculatorService: CalculatorService,
    private _dateTimeService: DateTimeService,
    private _dialogService: DialogService,
    private _translateService: TranslateService,
    private _moneyService: MoneyService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.documentType || changes.account) &&
      this.documentType &&
      this.account
    ) {
      const documentTypeKey = `${this.documentType}`.toUpperCase();

      this.documentTotalLabel = this._translateService.instant(
        `DIALOG.DEPOSIT_REQUEST.TOTAL.${documentTypeKey}`
      );
      this.addDepositForFutureLabel = this._translateService.instant(
        `DIALOG.DEPOSIT_REQUEST.ADD_DEPOSIT_FOR_FUTURE.${documentTypeKey}`
      );
    }

    if (changes.deposit) {
      this.cancellable = !!this.deposit;
      this.resetTab();
    }

    if (changes.documentCurrencyCode && this.documentCurrencyCode) {
      this.tabs = this._depositRequestDialogBuilder.buildTabs(
        this._moneyService.getCurrencyInfo(this.documentCurrencyCode).symbol,
        this.trackingScreenName
      );
    }

    if (
      (changes.documentCalculationResult ||
        changes.documentLocale ||
        changes.documentCurrencyCode) &&
      this.documentCalculationResult &&
      this.documentLocale &&
      this.documentCurrencyCode
    ) {
      const errorMessages = this._depositRequestDialogBuilder.buildErrorMessages(
        this.documentCalculationResult.outstandingBalance,
        this.documentCurrencyCode,
        this.documentLocale
      );

      this.amountErrorMessages = errorMessages.amount;
      this.percentageErrorMessages = errorMessages.percentage;
    }

    if (
      (changes.featureToggles ||
        changes.documentAllAccess ||
        changes.depositRequestFeatureName) &&
      this.featureToggles &&
      this.depositRequestFeatureName
    ) {
      this.depositSavable =
        this.documentAllAccess &&
        this.featureToggles.isAllAccess(this.depositRequestFeatureName);
      this.depositToggleAccess =
        this.documentAllAccess &&
        this.featureToggles.isAllAccess(Toggles.KEYS.DEPOSIT_TOGGLE);
    }

    const depositFormChanges =
      changes.deposit ||
      changes.documentCalculationResult ||
      changes.documentDate ||
      changes.documentDueDate ||
      changes.addDepositForFuture;

    if (depositFormChanges && this.documentCalculationResult) {
      const dueDateDatePickerOptions = this.documentDueDate
        ? { maxDate: this.documentDueDate }
        : null;
      const newDeposit: Deposit = this._depositRequestDialogBuilder.buildNewDeposit(
        this.documentCalculationResult,
        this.documentDueDate || this._dateTimeService.formatDate(),
        (this.addDepositForFuture && this.addDepositForFuture.value) ||
          DEFAULT_PERCENTAGE
      );
      const depositPayment: DepositPayment = this.documentCalculationResult
        .payments.deposit;
      const outstandingBalance: number = this.documentCalculationResult
        .outstandingBalance;
      const existingUnpaidDepositPayment: boolean =
        !!this.deposit && !!depositPayment && depositPayment.isUnpaid();
      const formKeys = this._depositRequestDialogBuilder.FORM_KEYS;
      const form: FormGroup = this._depositRequestDialogBuilder.buildForm(
        existingUnpaidDepositPayment ? this.deposit : newDeposit,
        outstandingBalance,
        existingUnpaidDepositPayment ? depositPayment : null,
        this.addDepositForFuture && this.addDepositForFuture.toggleState
      );

      this.formKeys = formKeys;
      this.depositDateDatePickerOptions = dueDateDatePickerOptions;

      if (!this.form) {
        this._startingFormValue = form.value;
        this._percentagePercentControl = form.get(formKeys.PERCENTAGE_PERCENT);
        this._percentageFixedAmountControl = form.get(
          formKeys.PERCENTAGE_FIXED_AMOUNT
        );
        this._fixedAmountControl = form.get(formKeys.FIXED_AMOUNT);
        this._dueDateControl = form.get(formKeys.DUE_DATE);
        this._addDepositForFutureControl = form.get(
          formKeys.ADD_DEPOSIT_FOR_FUTURE
        );

        this.form = form;

        merge(
          this._percentagePercentControl.valueChanges.pipe(
            tap((value: number) => {
              const deposit: Deposit = this._depositRequestDialogBuilder.buildDepositRequest(
                null,
                DepositTypesEnum.percentage,
                fromJS({
                  ...this.form.value,
                  [this.formKeys.PERCENTAGE_PERCENT]: value,
                })
              );
              const depositPayment: DepositPayment = this._calculatorService.calculateDepositAmountAndPercentage(
                deposit,
                this.documentCalculationResult
              );

              this._percentageFixedAmountControl.setValue(
                depositPayment.totalAmount,
                {
                  emitEvent: false,
                }
              );
              this._fixedAmountControl.setValue(depositPayment.totalAmount, {
                emitEvent: false,
              });
            }),
            map((): DepositTypesEnum => DepositTypesEnum.percentage)
          ),
          this._percentageFixedAmountControl.valueChanges.pipe(
            tap((value: number) => {
              const deposit: Deposit = this._depositRequestDialogBuilder.buildDepositRequest(
                null,
                DepositTypesEnum.fixed_amount_percentage_screen,
                fromJS({
                  ...this.form.value,
                  [this.formKeys.PERCENTAGE_FIXED_AMOUNT]: value,
                })
              );
              const depositPayment: DepositPayment = this._calculatorService.calculateDepositAmountAndPercentage(
                deposit,
                this.documentCalculationResult
              );

              this._percentagePercentControl.setValue(
                depositPayment.percentage > 100 ? 0 : depositPayment.percentage,
                { emitEvent: false }
              );
              this._fixedAmountControl.setValue(depositPayment.totalAmount, {
                emitEvent: false,
              });
            }),
            map(
              (): DepositTypesEnum =>
                DepositTypesEnum.fixed_amount_percentage_screen
            )
          ),
          this._fixedAmountControl.valueChanges.pipe(
            tap((value: number) => {
              const deposit: Deposit = this._depositRequestDialogBuilder.buildDepositRequest(
                null,
                DepositTypesEnum.fixed_amount,
                fromJS({
                  ...this.form.value,
                  [this.formKeys.FIXED_AMOUNT]: value,
                })
              );
              const depositPayment: DepositPayment = this._calculatorService.calculateDepositAmountAndPercentage(
                deposit,
                this.documentCalculationResult
              );

              this._percentagePercentControl.setValue(
                depositPayment.percentage,
                {
                  emitEvent: false,
                }
              );
              this._percentageFixedAmountControl.setValue(
                depositPayment.totalAmount,
                {
                  emitEvent: false,
                }
              );
            }),
            map((): DepositTypesEnum => DepositTypesEnum.fixed_amount)
          ),
          this._selectTab.asObservable().pipe(
            tap(() => {
              this.form.markAsDirty();

              forEach(this.form.controls, (formControl: AbstractControl) => {
                if (formControl.invalid) {
                  formControl.markAsDirty();
                }
              });
            }),
            map(
              (tab: DepositRequestTabsEnum): DepositTypesEnum =>
                tab === DepositRequestTabsEnum.percent
                  ? DepositTypesEnum.percentage
                  : DepositTypesEnum.fixed_amount
            )
          )
        )
          .pipe(takeUntil(this._destroyed))
          .subscribe(
            (depositType: DepositTypesEnum) =>
              (this._lastModifiedDepositType = depositType)
          );
      } else {
        const depositAmountPaid: number = get(
          depositPayment,
          DepositPayment.KEYS.AMOUNT_PAID,
          0
        );
        const maxAmount: number = outstandingBalance + depositAmountPaid;
        const validators: IFormValidators = this._depositRequestDialogBuilder.buildValidators(
          maxAmount
        );
        const noEmitEvent = { emitEvent: false };

        this._startingFormValue = form.value;

        this.form.reset(this._startingFormValue, noEmitEvent);

        this._percentagePercentControl.setValidators(
          validators[formKeys.PERCENTAGE_PERCENT]
        );
        this._percentageFixedAmountControl.setValidators(
          validators[formKeys.PERCENTAGE_FIXED_AMOUNT]
        );
        this._fixedAmountControl.setValidators(
          validators[formKeys.FIXED_AMOUNT]
        );

        this._percentagePercentControl.markAsDirty();
        this._percentageFixedAmountControl.markAsDirty();
        this._fixedAmountControl.markAsDirty();
        this._addDepositForFutureControl.markAsDirty();
      }

      // we have to individually enable/disable each control because of a bug where
      // enabling/disabling the entire form with options doesn't pass down
      // the options to each child control
      if (this.depositSavable) {
        this._percentagePercentControl.enable({ emitEvent: false });
        this._percentageFixedAmountControl.enable({ emitEvent: false });
        this._fixedAmountControl.enable({ emitEvent: false });
        this._dueDateControl.enable({ emitEvent: false });
        this._addDepositForFutureControl.enable({ emitEvent: false });
      } else {
        this._percentagePercentControl.disable({ emitEvent: false });
        this._percentageFixedAmountControl.disable({ emitEvent: false });
        this._fixedAmountControl.disable({ emitEvent: false });
        this._dueDateControl.disable({ emitEvent: false });
        this._addDepositForFutureControl.disable({ emitEvent: false });
      }
    }
  }

  ngOnInit() {
    this.DepositRequestTabsEnum = DepositRequestTabsEnum;
    this.dialogType = DIALOG_TYPE;
    this.primaryActionTapped = this._trackingActionBuilder.buildButtonTapped(
      'add_deposit',
      this.trackingScreenName
    );
    this.secondaryActionTapped = this._trackingActionBuilder.buildButtonTapped(
      'cancel_deposit',
      this.trackingScreenName
    );
  }

  ngAfterViewInit() {
    this._dialogService.dialogTriggers$
      .pipe(
        takeUntil(this._destroyed),
        filter(
          ({ type, action }: IDialogTrigger) =>
            type === DIALOG_TYPE && action === DialogActionsEnum.close
        ),
        filter(({ data }: IDialogTrigger) => {
          const { result } = data as IDialogData;

          return result === null;
        }),
        delay(DIALOG_ANIMATE_TIME)
      )
      .subscribe(() => {
        if (this.form.dirty) {
          this.resetTab();
        }

        this.form.reset(this._startingFormValue, { emitEvent: false });
      });
  }

  selectTab(tab: DepositRequestTabsEnum) {
    this.selectedTab = tab;

    this._selectTab.next(tab);
  }

  resetTab() {
    this.selectTab(
      this.deposit && this.deposit.isNonPercentageScreen()
        ? DepositRequestTabsEnum.fixed
        : DepositRequestTabsEnum.percent
    );
  }

  saveDepositRequest() {
    if (this.form.valid && this.depositSavable) {
      const deposit: Deposit = this._depositRequestDialogBuilder.buildDepositRequest(
        this.deposit ? this.deposit.id : '',
        this._lastModifiedDepositType,
        fromJS(this.form.value)
      );

      this.onSaveRequest.emit(deposit);

      if (
        [
          DepositTypesEnum.percentage,
          DepositTypesEnum.fixed_amount_percentage_screen,
        ].includes(deposit.type)
      ) {
        this.onUpdateDepositToggleSettings.emit(
          this._depositRequestDialogBuilder.buildDepositToggleProps(
            this.documentType,
            this._addDepositForFutureControl.value,
            deposit.percentage
          )
        );
      }
    }
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
